import { createStore, applyMiddleware, combineReducers } from 'redux';
import thunkMiddleware from 'redux-thunk';
// import logger from 'redux-logger';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';

// Legacy reducers (re-exported via slices for consistent API)
// eslint-disable-next-line import/no-cycle
import { loginReducer, settingsReducer } from './slices';

// Feature-specific Redux Toolkit slices
import Campaigns from './components/Campaigns/Campaigns.slice';
import Personas from './components/Persona/Personas.slice';
import MediaRanking from './components/MediaRanking/MediaRanking.slice';
import MediaOptimization from './components/MediaOptimization/MediaOptimization.slice';
import Layout from './_metronic/layout/Layout.slice';
import UniversalPersona from './components/Persona/UniversalPersona.slice';
import Ecommerce from './components/ECommerce/ECommerce.slice';
import CompetitiveIntel from './components/CompetitiveIntel/CompetitiveIntel.slice';
import Workspace from './components/Workspace/Workspace.slice';
import UniversalDiscovery from './components/UniversalDiscovery/slices/UniversalDiscovery.slice';
import Audiences from './components/Audiences/Audiences.slice';
import IntelligenceReport from './components/IntelligenceReport/IntelligenceReport.slice';
import Strategies from './components/Strategies/Strategies.slice';
import ReportCompetitiveIntel from './components/IntelligenceReport/CompetitiveIntel/reportCompetitiveIntel.slice';
import CompetitiveIntelV2 from './components/IntelligenceReport/CompetitiveIntel/competitiveIntelV2.slice';
import ReportAudiences from './components/IntelligenceReport/Audiences/reportAudiences.slice';
import Audits from './components/Audits/Audits.slice';
import CampaignWizard from './components/Campaigns/CampaignWizard/CampaignWizard.slice';
import Outreach from './components/Campaigns/Outreach/Outreach.slice';
import BrandSafety from './components/BrandSafety/BrandSafety.slice';
import Gmail from './components/Settings/Integration/Gmail.slice';
import CampaignPerformance from './components/Campaigns/Dashboard/Performance/CampaignPerformance.slice';
import CulturalIntelligence from './components/CulturalIntelligence/CulturalIntelligence.slice';

import { authChecker, alphabetSorter, debugMiddleware } from './middleware';
import { LOG_OUT } from './constants/actionTypes';

/**
 * SECURITY: Root reducer wrapper for complete state reset on logout.
 *
 * This ensures ALL Redux state is cleared when a user logs out,
 * preventing data leakage between user sessions. Without this, user A's data
 * (campaigns, influencer lists, drafts, settings) would persist in memory and
 * potentially be visible to user B after logout/login.
 *
 * This is a centralized solution that automatically handles:
 * - All 2 legacy reducers (login, settings)
 * - All 12 Redux Toolkit slices (Campaigns, Personas, etc.)
 * - Any future reducers added to the store
 *
 * Migration path: Legacy reducers are re-exported via src/slices/ with selectors
 * for a consistent API. Over time, these can be converted to full Redux Toolkit slices.
 */
const appReducer = combineReducers({
  // Legacy reducers (re-exported via slices for consistent import path)
  login: loginReducer,
  settings: settingsReducer,

  // Feature-specific Redux Toolkit slices
  Campaigns,
  Personas,
  MediaRanking,
  MediaOptimization,
  Layout,
  UniversalPersona,
  UniversalDiscovery,
  Ecommerce,
  CompetitiveIntel,
  Workspace,
  Audiences,
  IntelligenceReport,
  Strategies,
  ReportCompetitiveIntel,
  CompetitiveIntelV2,
  ReportAudiences,
  Audits,
  CampaignWizard,
  Outreach,
  BrandSafety,
  Gmail,
  CampaignPerformance,
  CulturalIntelligence,
});

/**
 * SECURITY: Root reducer that resets ALL state on logout.
 *
 * When LOG_OUT action is dispatched, we pass `undefined` as state to appReducer,
 * which causes ALL reducers to return their initial state. This provides
 * complete data isolation between user sessions.
 */
const rootReducer = (state, action) => {
  // SECURITY: Reset ALL state on logout to prevent data leakage
  if (action.type === LOG_OUT) {
    // Pass undefined to force all reducers to return their initial state
    // eslint-disable-next-line no-param-reassign
    state = undefined;
  }
  return appReducer(state, action);
};

const store = createStore(
  rootReducer,
  composeWithDevTools(
    applyMiddleware(
      thunkMiddleware,
      authChecker,
      alphabetSorter,
      debugMiddleware
      // logger,
    )
  )
);

store.subscribe(() => {
  // Reserved for future state change listeners
});

export default store;
